import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  countryCodes,
  InlineEdit,
  useFieldOptions,
  fieldOptionList,
} from "../components/Utils";
import axios from "../axiosConfig";
import Modal from "../components/Modal";
import AccountSelector from "../components/AccountSelector";
import Table from "../components/Table";
import "./Accounts.css";
import PageTitle from "../components/PageTitle";

const LOCATION_FIELDS = ["country", "switchboard_number"];
const CODED_FIELDS = ["status", "category", "sales_channel"];

const initialFormState = {
  name: "",
  name_short: "",
  name_long: "",
  parent: null,
  sector: "",
  category: "",
  website: "",
  status: "Target",
  sales_channel: "",
};

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const fieldOptions = useFieldOptions();
  const [showAllColumns, setShowAllColumns] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/accounts/", { withCredentials: true });

      // Extract country and switchboard from primary_location for table use
      const processedAccounts = response.data.map((account) => ({
        ...account,
        country: account.primary_location?.country || null,
        switchboard_number:
          account.primary_location?.switchboard_number || null,
      }));

      setAccounts(processedAccounts);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData(initialFormState);
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!formData.name_short.trim()) return;

    setSaving(true);
    try {
      const response = await axios.post("/accounts/", formData, {
        withCredentials: true,
      });

      if (response.data.success) {
        setShowModal(false);
        setSubmitAttempted(false);
        resetForm();
        fetchAccounts();
      }
    } catch (error) {
      console.error("Error creating account:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleInlineSave = async (row, field, newValue) => {
    try {
      if (LOCATION_FIELDS.includes(field)) {
        const locId = row.primary_location?.id;
        if (!locId) return;
        const res = await axios.patch(
          `/locations/${locId}/`,
          { [field]: newValue },
          {
            withCredentials: true,
          },
        );
        if (res.data.success) {
          setAccounts((prev) =>
            prev.map((a) =>
              a.id === row.id
                ? {
                    ...a,
                    [field]: newValue,
                    primary_location: a.primary_location
                      ? { ...a.primary_location, [field]: newValue }
                      : a.primary_location,
                  }
                : a,
            ),
          );
        }
        return;
      }

      const value = CODED_FIELDS.includes(field) ? newValue || null : newValue;
      const res = await axios.patch(
        `/accounts/${row.id}/`,
        { [field]: value },
        {
          withCredentials: true,
        },
      );
      if (res.data.success) {
        setAccounts((prev) =>
          prev.map((a) => (a.id === row.id ? { ...a, [field]: value } : a)),
        );
      }
    } catch (error) {
      console.error("Failed to update account field:", error);
    }
  };

  const baseColumns = [
    {
      key: "name_short",
      header: "Name",
      type: "alphabetical",
      fixed: true,
      width: 112,
      render: (value, row) => (
        <InlineEdit
          field="name_short"
          value={row.name_short || ""}
          displayValue={
            <Link to={`/account?id=${row.id}`}>{row.name_short}</Link>
          }
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
    {
      key: "name_long",
      header: "Long Name",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="name_long"
          value={row.name_long || ""}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
    {
      key: "parent",
      header: "Parent Company",
      type: "static",
      width: 128,
      render: (value, row) => (
        <InlineEdit
          field="parent"
          type="account"
          value={row.parent}
          displayValue={
            row.parent?.id ? (
              <Link to={`/account?id=${row.parent.id}`}>
                {row.parent.name_short}
              </Link>
            ) : (
              ""
            )
          }
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      type: "discrete",
      width: 96,
      render: (value, row) => (
        <InlineEdit
          field="status"
          type="dropdown"
          value={row.status || ""}
          options={fieldOptionList(fieldOptions, "accounts", "status")}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
    {
      key: "country",
      header: "Country",
      type: "discrete",
      width: 128,
      render: (value, row) => (
        <InlineEdit
          field="country"
          type="dropdown"
          value={row.country || ""}
          options={countryCodes.map((c) => ({ value: c.name, label: c.name }))}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
          disabled
          disabled_reason="Change primary location country on account page"
        />
      ),
    },
    {
      key: "switchboard",
      header: "Switchboard",
      type: "static",
      sortable: false,
      searchable: false,
      width: 128,
      render: (value, row) => (
        <InlineEdit
          field="switchboard_number"
          type="phone"
          value={row.switchboard_number || ""}
          phoneCountry={row.country}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
          disabled
          disabled_reason="Change primary location switchboard on account page"
        />
      ),
    },
    {
      key: "website",
      header: "Website",
      type: "static",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="website"
          type="link"
          value={row.website || ""}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
  ];

  const additionalColumns = [
    {
      key: "sector",
      header: "Sector",
      type: "discrete",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="sector"
          type="dropdown"
          value={row.sector || ""}
          options={fieldOptionList(fieldOptions, "accounts", "sector")}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
    {
      key: "category",
      header: "Category",
      type: "discrete",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="category"
          type="dropdown"
          value={row.category || ""}
          options={fieldOptionList(fieldOptions, "accounts", "category")}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
    {
      key: "sales_channel",
      header: "Sales Channel",
      type: "discrete",
      width: 144,
      render: (value, row) => (
        <InlineEdit
          field="sales_channel"
          type="dropdown"
          value={row.sales_channel || ""}
          options={fieldOptionList(fieldOptions, "accounts", "sales_channel")}
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
    {
      key: "temp_last_engagement",
      header: "Last Job",
      type: "date",
      width: 128,
      render: (value, row) => (
        <InlineEdit
          field="temp_last_engagement"
          type="date"
          value={
            typeof row.temp_last_engagement === "string"
              ? row.temp_last_engagement.slice(0, 10)
              : ""
          }
          onSave={(field, newValue) => handleInlineSave(row, field, newValue)}
        />
      ),
    },
  ];

  const columns = showAllColumns
    ? [...baseColumns, ...additionalColumns]
    : baseColumns.filter((column) => column.key !== "parent");

  return (
    <div className="page-layout">
      <PageTitle>Accounts</PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          <button onClick={() => setShowAllColumns((prev) => !prev)}>
            {showAllColumns ? "Show Less" : "Show All"}
          </button>
          <button
            onClick={() => {
              setSubmitAttempted(false);
              setShowModal(true);
            }}
          >
            + New Account
          </button>
        </div>
      </div>

      <div className="page-content">
        <Table
          data={accounts}
          columns={columns}
          loading={loading}
          emptyMessage="No accounts found."
          defaultSortColumn="name_short"
          defaultSortDirection="asc"
          fillPage
        />

        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSubmitAttempted(false);
          }}
          title="Create New Account"
        >
          <form
            className="account-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <label>
              <span>
                Short Name <em>(required)</em>:
              </span>
              <input
                autoComplete="off"
                type="text"
                name="name_short"
                value={formData.name_short}
                onChange={handleChange}
                className={
                  submitAttempted && !formData.name_short.trim()
                    ? "required-invalid"
                    : ""
                }
              />
            </label>

            <label>
              Name:
              <input
                autoComplete="off"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </label>

            <label>
              Long Name:
              <input
                autoComplete="off"
                type="text"
                name="name_long"
                value={formData.name_long}
                onChange={handleChange}
              />
            </label>

            <label>
              Parent Account:
              <AccountSelector
                value={formData.parent}
                onChange={(account) =>
                  setFormData((prev) => ({
                    ...prev,
                    parent: account ? account.id : null, // Store only ID in formData.parent
                  }))
                }
              />
            </label>

            <label>
              Sector:
              <select
                autoComplete="off"
                name="sector"
                value={formData.sector}
                onChange={handleChange}
              >
                <option value="">—</option>
                {fieldOptionList(fieldOptions, "accounts", "sector").map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Category:
              <select
                autoComplete="off"
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">—</option>
                {fieldOptionList(fieldOptions, "accounts", "category").map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Sales Channel:
              <select
                autoComplete="off"
                name="sales_channel"
                value={formData.sales_channel}
                onChange={handleChange}
              >
                <option value="">—</option>
                {fieldOptionList(fieldOptions, "accounts", "sales_channel").map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Website:
              <input
                autoComplete="off"
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
              />
            </label>

            <label>
              Status:
              <select
                autoComplete="off"
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="">—</option>
                {fieldOptionList(fieldOptions, "accounts", "status").map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="modal-footer">
              <button type="submit" className="submit-btn" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  setSubmitAttempted(false);
                }}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default Accounts;
